import React from 'react';
import { ArrowLeft, Users, MessageCircle, Shield, Award, Heart, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const AboutPage = () => {
  const teamStats = [
    { label: "Founded", value: "2025", icon: Award },
    { label: "Team Members", value: "50+", icon: Users },
    { label: "Countries", value: "2+", icon: Globe },
    { label: "Customer Satisfaction", value: "99%", icon: Heart }
  ];

  const values = [
    {
      title: "Transparency",
      description: "We believe in complete transparency in group contributions and event planning.",
      icon: Shield
    },
    {
      title: "Community",
      description: "Building stronger communities through seamless group collaboration.",
      icon: Users
    },
    {
      title: "Innovation",
      description: "Constantly improving our WhatsApp integration and user experience.",
      icon: MessageCircle
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-green-100 hover:text-white mb-6 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4">About Michango</h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Revolutionizing group event planning and contribution management through seamless WhatsApp integration
            </p>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Michango App was born out of a simple observation: organizing group events and managing contributions 
                  shouldn't be complicated or stressful. Our founders experienced firsthand the challenges of 
                  coordinating payments for group trips, weddings, and celebrations.
                </p>
                <p>
                  Traditional methods often involved endless WhatsApp messages, confusing spreadsheets, and 
                  awkward money conversations. We knew there had to be a better way.
                </p>
                <p>
                  In 2025, we launched Michango with a mission to make group contribution management transparent, 
                  secure, and effortless. By integrating directly with WhatsApp – the platform people already 
                  use to plan events – we created a seamless solution that feels natural and familiar.
                </p>
                <p>
                  Today, thousands of users across Tanzania countries trust Michango App to manage their group events, 
                  from intimate birthday parties to large wedding celebrations. We're proud to have facilitated 
                  member contributions while maintaining our core values of transparency and simplicity.
                </p>
              </div>
            </div>
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-6">
                {teamStats.map((stat) => {
                  const IconComponent = stat.icon;
                  return (
                    <div key={stat.label} className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-green-600 rounded-full mb-3">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-green-600">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do at Michango App
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <div key={value.title} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-xl mb-6">
                    <IconComponent className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-green-600 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-green-100 text-lg leading-relaxed">
                To eliminate the stress and confusion from group event planning by providing a transparent, 
                secure, and user-friendly platform that integrates seamlessly with WhatsApp – making every 
                celebration more enjoyable and every contribution more meaningful.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Our Vision</h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                To become the global standard for group contribution management, empowering communities 
                worldwide to come together and create memorable experiences without the financial friction 
                that often accompanies group planning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Integration Focus */}
      <section className="py-16 bg-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600 rounded-full mb-6">
              <MessageCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why WhatsApp Integration?</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              WhatsApp is where people naturally plan their events. By integrating directly with WhatsApp, 
              we eliminate the need to switch between multiple apps or platforms. Share contribution requests, 
              send payment reminders, and update your group – all within the familiar WhatsApp environment 
              your group already uses.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">2B+</div>
              <div className="text-gray-600">WhatsApp Users Worldwide</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">100B+</div>
              <div className="text-gray-600">Messages Sent Daily</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">180+</div>
              <div className="text-gray-600">Countries Using WhatsApp</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Have questions about Michango App? Want to partner with us? We'd love to hear from you.
          </p>
          
          <div className="bg-gray-50 rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="space-y-4">
              <div className="text-gray-700"><strong>Email:</strong> info@michango.app</div>
              <div className="text-gray-700"><strong>Phone:</strong> +255 741 400 600</div>
              <div className="text-gray-700"><strong>Address:</strong> Dar es Salaam, Tanzania</div>
              <div className="text-gray-700"><strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM</div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home Button */}
      <div className="py-8 bg-gray-50 text-center">
        <Link to="/">
          <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg">
            Back to Home
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default AboutPage;
